/* Catalog membership presentation.
 *
 * Account authentication and Pup Club entitlement are deliberately separate:
 * an authenticated customer must never see a contradictory Sign in prompt,
 * and an active entitlement is the only condition that may reveal the Pup
 * Club price. This is presentation only. Checkout enforcement remains a
 * server-side Phoenix/Supabase concern and is never fabricated here.
 */
(() => {
  'use strict';

  const money = cents => `$${(Number(cents || 0) / 100).toFixed(2)}`;
  const product = id => window.PK_SHOPIFY_CATALOG?.products?.[id]
    || window.PK_VIP_PRICE_MAP?.products?.[id]
    || null;
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const route = fragment => `/?view=pup-club#${fragment}`;

  /* Joining adds the published Phoenix trial variant. Phoenix owns its recurring
     schedule; a Shopify-native selling plan is not required for that record. */
  function membershipPurchasable() {
    const item = window.PK_SHOPIFY_CATALOG?.membership;
    const id = value => Number.isInteger(value) && value > 0;
    return !!(item && id(item.variantId) && (item.billingProvider === 'phoenix' || id(item.sellingPlanId)));
  }

  function accountState() {
    const auth = window.PK_PUP_CLUB_AUTH || {};
    return {
      authenticated: auth.authenticated === true,
      activeMember: auth.authenticated === true && auth.activeMember === true,
      status: String(auth.status || '').toLowerCase(),
      /* Pup Club is in the cart (pk-club-cart-state.js): show the Pup Club price (Zack, 2026-09-27). */
      clubInCart: window.PK_CLUB_IN_CART === true,
    };
  }

  function link(label, fragment) {
    const node = make('a', 'pk-pricing-contract__link', label);
    node.href = route(fragment);
    return node;
  }

  /* Only statements the verified status supports. No saving, unlock or other
     benefit promise is made to a customer who is not an active member. */
  function statusNote(state) {
    if (state.status === 'canceled') return 'Your Pup Club benefits are inactive.';
    if (state.status === 'paused' || state.status === 'past_due') return 'Your Pup Club pricing is unavailable until your membership is active.';
    if (state.status === 'unknown') return 'Your account is signed in. Pup Club pricing stays protected until your status is verified.';
    return '';
  }

  const verifiedDemo = () => {
    try { return window.PKPupClub?.signedIn() === true && window.PKPupClub?.isReviewAccount() === true; } catch (_) { return false; }
  };
  function renderSlot(slot, live, state) {
    const publicCents = Number(live?.publicCents || 0);
    const vipCents = Number(live?.vipCents || 0);
    if (!publicCents) return;

    const purchasable = membershipPurchasable();
    const key = [
      state.authenticated ? 'account' : 'guest',
      state.activeMember ? 'active' : 'inactive',
      state.status || 'none',
      purchasable ? 'join' : 'nojoin',
      state.clubInCart ? 'incart' : 'nocart',
      verifiedDemo() ? 'review' : 'ordinary',
      window.PK_CUSTOMER?.loggedIn === true ? 'shopify' : 'no-shopify',
      publicCents,
      vipCents,
    ].join(':');
    if (slot.dataset.pkCatalogPriceKey === key) return;

    slot.dataset.pkCatalogPriceKey = key;
    slot.classList.add('pk-catalog-price--contract');
    slot.replaceChildren();
    slot.classList.remove('pk-catalog-price--incart');

    // Member prices need the Shopify sign-in too: checkout only applies the member 50% to a signed-in subscriber (Zack, 2026-09-27).
    if (state.activeMember && (window.PK_CUSTOMER?.loggedIn === true || verifiedDemo()) && vipCents > 0 && vipCents < publicCents) {
      slot.append(
        make('span', 'pk-catalog-member-label', 'Active Pup Club price'),
        make('strong', 'pk-catalog-member-price', money(vipCents)),
        make('span', 'pk-catalog-public-price', `Standard price ${money(publicCents)}`),
        make('span', 'pk-catalog-saving', `You save ${money(publicCents - vipCents)}`),
      );
      return;
    }

    /* Pup Club in the cart: the shopper has joined, so the card shows the price
       this order gets with the membership. Removing it restores the standard price. */
    const inCartPrice = state.clubInCart && !state.authenticated && !verifiedDemo() && vipCents > 0 && vipCents < publicCents;
    slot.classList.toggle('pk-catalog-price--incart', inCartPrice);
    if (inCartPrice) {
      slot.append(
        make('span', 'pk-catalog-member-label', 'Your Pup Club price'),
        make('strong', 'pk-catalog-member-price', money(vipCents)),
        make('span', 'pk-catalog-saving', `You save ${money(publicCents - vipCents)}`),
      );
      return;
    }

    /* Everyone without an active entitlement pays, and is shown, the standard
       price. A guest gets no Sign in link here. The price block carries no plain
       Account link (it read as part of the price); an account whose Pup Club
       status needs attention gets one named link to the page that handles it. */
    slot.append(
      make('span', 'pk-catalog-standard-label', 'Standard price'),
      make('strong', 'pk-catalog-standard-price', money(publicCents)),
    );

    const note = state.authenticated ? statusNote(state) : '';
    if (note) slot.append(make('span', 'pk-catalog-price-note', note));

    const links = make('span', 'pk-pricing-contract__links');
    if (note) links.append(link('Manage Pup Club', 'manage'));
    /* A join link is shown only to a signed-out shopper (2026-10-09, one join rule:
       a signed-in account cannot join). Accounts are routed to their account. */
    if (purchasable && !state.activeMember && !verifiedDemo() && !state.clubInCart && !state.authenticated) {
      links.append(link('Join Pup Club', 'offer'));
    }
    if (links.childElementCount) slot.append(links);
  }

  function render() {
    const root = document.querySelector('.pk-vip-portal');
    if (!root) return;
    const state = accountState();
    root.querySelectorAll('.product-card .price-slot[data-pk-price], .product-detail .price-slot[data-pk-price]').forEach(slot => {
      renderSlot(slot, product(slot.dataset.pkPrice), state);
    });
  }

  render();
  addEventListener('hashchange', render);
  addEventListener('popstate', render);
  addEventListener('pk:auth-state', render);
  addEventListener('pk:member-state', render);
  addEventListener('pk:club-cart', render);
  new MutationObserver(render).observe(document.body, { childList: true, subtree: true });
})();
